import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CorporateDirector } from '@core/models/admin/account';
import { CorporateRegistrationService } from '@core/services/corporate-registration.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import {
  ButtonDirective,
  SpinDirective,
} from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import { Observable, Subscription } from 'rxjs';
import { LoadingService } from '@core/services/loading.service';
import { CustomValidators } from '@core/validators/custom-validator';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  selector: 'app-edit-director-details',
  standalone: true,
  imports: [
    CommonModule,
    KycAccordionComponent,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    ButtonDirective,
    SpinDirective,
    DropdownComponent,
  ],
  templateUrl: './edit-director-details.component.html',
  styleUrl: './edit-director-details.component.scss',
})
export class EditDirectorDetailsComponent implements OnInit, OnDestroy {
  @Input() directorDetails!: CorporateDirector[] | null;

  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);
  corporateService = inject(CorporateRegistrationService);
  cdRef = inject(ChangeDetectorRef);
  toast = inject(NotificationFacade);
  router = inject(Router);
  private loadingService = inject(LoadingService);
  confirmDialogService = inject(ConfirmDialogService);

  // isLoading: boolean = false;
  totalDirectors!: number;
  accountId!: string;
  subs: Subscription[] = [];
  actionList: DropDownModel[] = [];
  isLoading$!: Observable<boolean>;
  directorDetailsForm = this.fb.nonNullable.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone_number: ['', [Validators.required, CustomValidators.phoneNumber()]],
    bvn: ['', [Validators.required, CustomValidators.bvn()]],
    account_id: [this.accountId],
  });

  constructor() {
    this.isLoading$ = this.loadingService.getLoadingObservable(
      'add-corporate-director-kyc'
    );
  }

  ngOnInit(): void {
    this.setAccountId();
  }

  setAccountId() {
    this.accountId = this.route.snapshot.parent?.params['accountId'];
    this.directorDetailsForm.patchValue({
      account_id: this.accountId,
    });
  }

  addNewDirector(): void {
    const sub = this.corporateService
      .directorDetails(this.directorDetailsForm.getRawValue())
      .subscribe({
        next: (response) => {
          this.directorDetails = response.data;
          this.cdRef.detectChanges();
          this.toast.success('Successfully added director');
        },
        error: (resp) => {
          this.toast.error('Failed to add director details');
        },
      });

    this.subs.push(sub);
  }

  resetForm() {
    // this.directorDetailsForm.reset();
    this.directorDetailsForm.patchValue({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      bvn: '',
      account_id: this.accountId,
    });

    this.cdRef.detectChanges();
  }

  initDropdownList(director: CorporateDirector) {
    return (this.actionList = [
      {
        action: () => {
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Activate Director',
            description: 'Are you sure you want to activate this director?',
            loadingText: 'Activating director, please wait...',
            action: () => {
              this.updateDirector(director.id, 'active');
            },
          });
        },
        title: 'Activate',
        show: director.status != 'active',
      },
      {
        action: () => {
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, deactivate Director',
            description: 'Are you sure you want to deactivate this director?',
            loadingText: 'Activating director, please wait...',
            action: () => {
              this.updateDirector(director.id, 'inactive');
            },
          });
        },
        title: 'Deactivate',
        show: director.status === 'active',
      },
    ]);
  }

  updateDirector(diretorId: string, status: "inactive" | "active") {
    const sub = this.corporateService.updateDirectorStatus({account_id: this.accountId, director_id: diretorId, status:status}).subscribe({
       next: (response) => {
          this.directorDetails = response.data;
          this.cdRef.detectChanges();
          this.toast.success('Successfully updated director status');
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
        },
        error: (resp) => {
          this.toast.error('Failed to update director status');
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
        },
    });
    this.subs.push(sub)
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
